import { useEffect, useState } from "react";
import { Badge, Btn, Card, Dot, I } from "../ui";
import { admin, useSession } from "../auth-client";
import { isSysadminRole, type PlatformRole } from "../../api/platform-roles";

interface UserRow {
  id: string;
  email: string;
  name: string;
  role: string | null;
  banned?: boolean | null;
  banReason?: string | null;
  createdAt: Date | string;
}

export function Users({ pushToast }: { pushToast: (k: "ok" | "err", m: string) => void }) {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState<PlatformRole>("user");
  // Solo un sysadmin vede e assegna il ruolo sysadmin (il server lo impone).
  const { data: session } = useSession();
  const canManageSysadmins = isSysadminRole(session?.user?.role);

  const reload = async () => {
    try {
      setLoading(true);
      const res = await admin.listUsers({ query: { limit: 200 } });
      if (res.error) throw new Error(res.error.message ?? "Errore");
      const list = (res.data?.users ?? []) as unknown as UserRow[];
      setUsers(list);
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore caricamento utenti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void reload(); }, []);

  const onCreate = async () => {
    if (!newEmail || !newPassword) {
      pushToast("err", "Email e password richiesti");
      return;
    }
    try {
      const res = await admin.createUser({
        email: newEmail,
        password: newPassword,
        name: newName || newEmail.split("@")[0],
        role: newRole,
      });
      if (res.error) throw new Error(res.error.message ?? "Errore");
      pushToast("ok", `Utente ${newEmail} creato`);
      setNewEmail(""); setNewName(""); setNewPassword(""); setNewRole("user");
      setShowAdd(false);
      await reload();
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore create");
    }
  };

  const onSetRole = async (u: UserRow, role: PlatformRole) => {
    try {
      const res = await admin.setRole({ userId: u.id, role });
      if (res.error) throw new Error(res.error.message ?? "Errore");
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, role } : x)));
      pushToast("ok", `Ruolo di ${u.email} → ${role}`);
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore");
    }
  };

  const onBan = async (u: UserRow) => {
    const reason = prompt(`Motivo ban per ${u.email}?`, "Violazione ToS");
    if (reason === null) return;
    try {
      const res = await admin.banUser({ userId: u.id, banReason: reason });
      if (res.error) throw new Error(res.error.message ?? "Errore");
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, banned: true, banReason: reason } : x)));
      pushToast("ok", `${u.email} bannato`);
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore");
    }
  };

  const onUnban = async (u: UserRow) => {
    try {
      const res = await admin.unbanUser({ userId: u.id });
      if (res.error) throw new Error(res.error.message ?? "Errore");
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, banned: false, banReason: null } : x)));
      pushToast("ok", `${u.email} sbannato`);
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore");
    }
  };

  const onDelete = async (u: UserRow) => {
    if (!confirm(`Eliminare definitivamente ${u.email}? Questa azione è irreversibile.`)) return;
    try {
      const res = await admin.removeUser({ userId: u.id });
      if (res.error) throw new Error(res.error.message ?? "Errore");
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      pushToast("ok", `${u.email} eliminato`);
    } catch (e) {
      pushToast("err", e instanceof Error ? e.message : "Errore");
    }
  };

  return (
    <div className="col gap-16">
      <Card
        title="Utenti"
        sub="gestione account: crea, assegna ruolo, banna ed elimina"
        actions={
          <div className="row gap-6">
            <Btn size="sm" variant="ghost" icon={I.refresh} onClick={() => void reload()}>Refresh</Btn>
            <Btn size="sm" variant="primary" icon={I.plus} onClick={() => setShowAdd(!showAdd)}>
              {showAdd ? "Annulla" : "Crea utente"}
            </Btn>
          </div>
        }
      >
        {showAdd && (
          <div className="preview-box" style={{ marginBottom: 14, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              <div className="field" style={{ flex: 1, minWidth: 200 }}>
                <label className="label">Email</label>
                <input className="input" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
              </div>
              <div className="field" style={{ flex: 1, minWidth: 180 }}>
                <label className="label">Nome</label>
                <input className="input" value={newName} onChange={(e) => setNewName(e.target.value)} />
              </div>
            </div>
            <div className="row gap-8" style={{ flexWrap: "wrap" }}>
              <div className="field" style={{ flex: 1, minWidth: 220 }}>
                <label className="label">Password iniziale</label>
                <input className="input" type="text" minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="min. 8 caratteri" />
              </div>
              <div className="field" style={{ width: 140 }}>
                <label className="label">Ruolo</label>
                <select className="select" value={newRole} onChange={(e) => setNewRole(e.target.value as PlatformRole)}>
                  <option value="user">user</option>
                  <option value="admin">admin</option>
                  {canManageSysadmins && <option value="sysadmin">sysadmin</option>}
                </select>
              </div>
            </div>
            <div className="row gap-8" style={{ justifyContent: "flex-end" }}>
              <Btn size="sm" variant="primary" onClick={() => void onCreate()}>Crea</Btn>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 20, color: "var(--text-2)", fontSize: 13 }}>Caricamento…</div>
        ) : users.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "var(--text-2)", fontSize: 13 }}>
            Nessun utente.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="tbl" style={{ width: "100%", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Email</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Nome</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Ruolo</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Stato</th>
                  <th style={{ textAlign: "left", padding: "8px 12px" }}>Creato</th>
                  <th style={{ textAlign: "right", padding: "8px 12px" }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} style={{ borderTop: "1px solid var(--line)" }}>
                    <td style={{ padding: "8px 12px" }}>{u.email}</td>
                    <td style={{ padding: "8px 12px" }}>{u.name}</td>
                    <td style={{ padding: "8px 12px" }}>
                      <select
                        className="select"
                        style={{ height: 26, fontSize: 12, padding: "0 8px" }}
                        value={u.role ?? "user"}
                        disabled={isSysadminRole(u.role) && !canManageSysadmins}
                        onChange={(e) => void onSetRole(u, e.target.value as PlatformRole)}
                      >
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                        {(canManageSysadmins || isSysadminRole(u.role)) && <option value="sysadmin">sysadmin</option>}
                      </select>
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {u.banned ? (
                        <div className="row gap-6" style={{ alignItems: "center" }} title={u.banReason ?? ""}>
                          <Dot kind="danger" />
                          <Badge kind="danger">banned</Badge>
                        </div>
                      ) : (
                        <div className="row gap-6" style={{ alignItems: "center" }}>
                          <Dot kind="live" />
                          <Badge kind="accent">attivo</Badge>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "8px 12px", color: "var(--text-2)" }}>
                      {new Date(u.createdAt).toLocaleDateString("it-IT")}
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                      {u.banned ? (
                        <Btn size="sm" variant="ghost" onClick={() => void onUnban(u)}>Sblocca</Btn>
                      ) : (
                        <Btn size="sm" variant="ghost" onClick={() => void onBan(u)}>Banna</Btn>
                      )}
                      <span style={{ display: "inline-block", width: 4 }} />
                      <Btn size="sm" variant="danger" onClick={() => void onDelete(u)}>×</Btn>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
